/**
 * The Types section and the destination rows (1.I-1 deliverable 8). Type rows are text with
 * their counts until a `/catalogue/$typeKey` route exists and the `catalogue` flag is on.
 */
import { flagsFromMe } from '@codex/contracts';
import { Button, type IconName, Skeleton, TYPE_KEYS, TypeGlyph, type TypeKey } from '@codex/ui';
import { useLocation, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { InboxCountBadge } from '../inbox/count-badge.js';
import {
  canonTypeRows,
  type Destination,
  INBOX_ROUTE,
  inboxVisible,
  referenceHref,
  referenceTypeRows,
  typeRowHref,
  visibleDestinations,
} from './destinations.js';
import { NavLink } from './nav-link.js';
import { useTypes } from './queries.js';
import { useShellSession } from './session.js';

const DESTINATION_ICON: Record<Destination['key'], IconName> = {
  types: 'stack',
  chronicle: 'calendar',
  secrets: 'lock',
  trash: 'x',
  settings: 'filter',
};

function isTypeKey(key: string): key is TypeKey {
  return (TYPE_KEYS as readonly string[]).includes(key);
}

export function useRoutePaths(): readonly string[] {
  const router = useRouter();
  return Object.keys(router.routesByPath);
}

export function SidebarSection({
  title,
  testId,
  children,
}: {
  title: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <section className="cx-nav-section" aria-label={title}>
      <div className="cx-nav-section__head">
        <h2 className="cx-nav-section__title">{title}</h2>
      </div>
      <ul className="cx-nav-section__list" data-testid={testId}>
        {children}
      </ul>
    </section>
  );
}

export function TypesSection() {
  const me = useShellSession();
  const routePaths = useRoutePaths();
  const types = useTypes();
  const flags = me ? flagsFromMe(me) : null;
  return (
    <section className="cx-nav-section" aria-label="Types">
      <div className="cx-nav-section__head">
        <h2 className="cx-nav-section__title">Types</h2>
      </div>
      {types.isPending ? (
        <Skeleton lines={4} variant="rows" label="Loading types" />
      ) : types.isError || !flags ? (
        <div className="cx-shell-list-error">
          <p role="alert">Couldn't load this list.</p>
          <Button size="sm" onClick={() => void types.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <ul className="cx-nav-section__list" data-testid="shell.sidebar.types">
          {canonTypeRows(types.data).map((type) => {
            const href = typeRowHref(type.key, routePaths, flags);
            const glyph = isTypeKey(type.key) ? (
              <TypeGlyph type={type.key} />
            ) : (
              <TypeGlyph type="note" />
            );
            const count = <span className="cx-nav-row__count">{type.documentCount}</span>;
            return (
              <li key={type.id} className="cx-nav-row-item" data-type-key={type.key}>
                {href ? (
                  <NavLink href={href} glyph={glyph} trailing={count}>
                    {type.namePlural}
                  </NavLink>
                ) : (
                  <div className="cx-nav-row cx-shell-static">
                    {glyph}
                    <span className="cx-nav-row__label">{type.namePlural}</span>
                    {count}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** The destination rows whose route is in the tree and whose flag is on (none when none are). */
export function DestinationRows({
  keys,
  title,
}: {
  keys: readonly Destination['key'][];
  title: string;
}) {
  const me = useShellSession();
  const routePaths = useRoutePaths();
  const pathname = useLocation({ select: (location) => location.pathname });
  if (!me) return null;
  const rows = visibleDestinations(routePaths, flagsFromMe(me)).filter((row) =>
    keys.includes(row.key),
  );
  if (rows.length === 0) return null;
  return (
    <SidebarSection title={title}>
      {rows.map((row) => (
        <li key={row.key} className="cx-nav-row-item">
          <NavLink
            href={row.path}
            icon={DESTINATION_ICON[row.key]}
            active={pathname === row.path || pathname.startsWith(`${row.path}/`)}
          >
            {row.label}
          </NavLink>
        </li>
      ))}
    </SidebarSection>
  );
}

/** The Reference section (`reference` flag): the reference-library types with their counts. */
export function ReferenceSection() {
  const me = useShellSession();
  const routePaths = useRoutePaths();
  const types = useTypes();
  if (!me || !flagsFromMe(me).reference) return null;
  const href = referenceHref(routePaths);
  return (
    <section className="cx-nav-section" aria-label="Reference">
      <div className="cx-nav-section__head">
        <h2 className="cx-nav-section__title">Reference</h2>
      </div>
      {types.isPending ? (
        <Skeleton lines={3} variant="rows" label="Loading reference types" />
      ) : types.isError ? (
        <div className="cx-shell-list-error">
          <p role="alert">Couldn't load this list.</p>
          <Button size="sm" onClick={() => void types.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <ul className="cx-nav-section__list" data-testid="shell.sidebar.reference">
          {referenceTypeRows(types.data).map((type) => {
            const glyph = <TypeGlyph type={isTypeKey(type.key) ? type.key : 'note'} />;
            const count = <span className="cx-nav-row__count">{type.documentCount}</span>;
            return (
              <li key={type.id} className="cx-nav-row-item" data-type-key={type.key}>
                {href ? (
                  <NavLink href={href} glyph={glyph} trailing={count}>
                    {type.namePlural}
                  </NavLink>
                ) : (
                  <div className="cx-nav-row cx-shell-static">
                    {glyph}
                    <span className="cx-nav-row__label">{type.namePlural}</span>
                    {count}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** The Inbox link: only with `/inbox` in the route tree and the `catalogue` flag; no count yet. */
export function InboxRow() {
  const me = useShellSession();
  const routePaths = useRoutePaths();
  const pathname = useLocation({ select: (location) => location.pathname });
  if (!me || !inboxVisible(routePaths, flagsFromMe(me))) return null;
  return (
    <ul className="cx-nav-section__list cx-nav-inbox">
      <li className="cx-nav-row-item">
        <NavLink
          href={INBOX_ROUTE}
          icon="inbox"
          testId="shell.sidebar.inbox"
          active={pathname === INBOX_ROUTE}
          trailing={<InboxCountBadge />}
        >
          Inbox
        </NavLink>
      </li>
    </ul>
  );
}
